import { useCallback, useRef, useState } from 'react';
import {
  GIF_ACCEPT,
  GIF_CONTENT_TYPES,
  MAX_ASSET_BYTES,
  MAX_ASSETS_PER_PACK,
  STICKER_ACCEPT,
  STICKER_CONTENT_TYPES,
} from '../lib/assets';
import { ApiError } from '../lib/api';
import {
  formatBytes,
  sha256HexOfFile,
  uploadAssetFile,
} from '../lib/uploadFile';
import { useUploadStore } from '../lib/uploadStore';
import type { PackKind } from '../lib/packs';

/**
 * Drag-drop asset uploader for the pack editor.
 *
 * Validates each file pre-flight (size, content type, per-pack quota),
 * computes its SHA-256 in the browser, then PUTs the bytes through the
 * Pages catch-all proxy so the Worker sees an authenticated upload.
 *
 * Per-file rows live in the Zustand `uploadStore` keyed by a temp id
 * chosen at drop time. Successful uploads stay until the parent calls
 * `clearDone()` after refetching the pack; failures show a "Thử lại"
 * button that re-runs only that file.
 *
 * Validates Requirements 18.1, 18.2, 18.3, 18.4, 22.2, 25.1.
 */

export interface AssetUploaderProps {
  packId: string;
  packKind: PackKind;
  /** Number of assets currently attached to the pack (from D1). */
  currentAssetCount: number;
  /** Whether uploads are allowed (e.g. pack must be in draft). */
  disabled?: boolean;
  /**
   * Called after every successful upload so the parent can refetch the
   * canonical asset list. The uploader does not optimistically insert
   * — the Worker is the source of truth for `position` / `width` /
   * `height` / `r2Key`.
   */
  onUploaded: () => void;
}

export default function AssetUploader({
  packId,
  packKind,
  currentAssetCount,
  disabled = false,
  onUploaded,
}: AssetUploaderProps): JSX.Element {
  const uploads = useUploadStore((s) => s.uploads);
  const addUpload = useUploadStore((s) => s.add);
  const setStatus = useUploadStore((s) => s.setStatus);
  const setProgress = useUploadStore((s) => s.setProgress);
  const setSha256 = useUploadStore((s) => s.setSha256);
  const setError = useUploadStore((s) => s.setError);
  const removeUpload = useUploadStore((s) => s.remove);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const allowedTypes =
    packKind === 'sticker' ? STICKER_CONTENT_TYPES : GIF_CONTENT_TYPES;
  const accept = packKind === 'sticker' ? STICKER_ACCEPT : GIF_ACCEPT;

  /**
   * Run the full upload pipeline for one file: hash → PUT → refetch.
   * Errors are caught and surfaced on the upload row; nothing throws to
   * the caller so multi-file drops don't abort halfway.
   */
  const runUpload = useCallback(
    async (tempUploadId: string, file: File) => {
      try {
        setStatus(tempUploadId, 'hashing');
        const sha256 = await sha256HexOfFile(file);
        setSha256(tempUploadId, sha256);

        setStatus(tempUploadId, 'uploading');
        await uploadAssetFile({
          packId,
          assetId: tempUploadId,
          file,
          sha256,
          onProgress: (fraction) => setProgress(tempUploadId, fraction),
        });

        setProgress(tempUploadId, 1);
        setStatus(tempUploadId, 'done');
        onUploaded();
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `${err.message}${err.requestId ? ` (${err.requestId})` : ''}`
            : err instanceof Error
              ? err.message
              : 'Tải lên thất bại';
        setError(tempUploadId, message);
      }
    },
    [packId, onUploaded, setError, setProgress, setSha256, setStatus],
  );

  /**
   * Validate a single file pre-flight. Returns a Vietnamese reason on
   * rejection or `null` on accept. The caller decides whether to surface
   * the reason (we render it inline as a transient row).
   */
  const validateFile = useCallback(
    (file: File, queuedSoFar: number): string | null => {
      if (!allowedTypes.has(file.type)) {
        return `Loại tệp ${file.type || 'không xác định'} không được hỗ trợ cho pack ${packKind}`;
      }
      if (file.size > MAX_ASSET_BYTES) {
        return `Tệp vượt quá ${formatBytes(MAX_ASSET_BYTES)}`;
      }
      const projected =
        currentAssetCount +
        queuedSoFar +
        // count items still in flight (not done, not error) toward the cap
        uploads.filter((u) => u.status !== 'done' && u.status !== 'error')
          .length;
      if (projected >= MAX_ASSETS_PER_PACK) {
        return `Pack đã đạt giới hạn ${MAX_ASSETS_PER_PACK} asset`;
      }
      return null;
    },
    [allowedTypes, currentAssetCount, packKind, uploads],
  );

  /** Common entry point for both drop and file-input flows. */
  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (disabled || files === null) return;
      let accepted = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (file === null) continue;
        const reason = validateFile(file, accepted);
        if (reason !== null) {
          // Reject file: push a synthetic row in `error` state so the
          // admin sees the reason without losing context. We assign a
          // temp id so the user can dismiss it.
          const tempId = crypto.randomUUID();
          addUpload({
            tempUploadId: tempId,
            fileName: file.name,
            byteSize: file.size,
            contentType: file.type,
            status: 'error',
          });
          setError(tempId, reason);
          continue;
        }
        const tempId = crypto.randomUUID();
        addUpload({
          tempUploadId: tempId,
          fileName: file.name,
          byteSize: file.size,
          contentType: file.type,
        });
        accepted += 1;
        // Fire-and-forget; row state covers progress / errors.
        void runUpload(tempId, file);
      }
    },
    [addUpload, disabled, runUpload, setError, validateFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  // For retry we need the original `File` object back. The drop handler
  // above is the only place we have it, so we keep a parallel ref-map
  // keyed by tempUploadId. Stored outside Zustand because `File` isn't
  // serialisable and we don't want it tracked by the devtools.
  const filesRef = useRef(new Map<string, File>());

  // Wrap addUpload + runUpload in a wrapper that also stashes the File.
  const handleFilesWithRetry = useCallback(
    (files: FileList | null) => {
      if (disabled || files === null) return;
      let accepted = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (file === null) continue;
        const reason = validateFile(file, accepted);
        if (reason !== null) {
          const tempId = crypto.randomUUID();
          addUpload({
            tempUploadId: tempId,
            fileName: file.name,
            byteSize: file.size,
            contentType: file.type,
            status: 'error',
          });
          setError(tempId, reason);
          continue;
        }
        const tempId = crypto.randomUUID();
        filesRef.current.set(tempId, file);
        addUpload({
          tempUploadId: tempId,
          fileName: file.name,
          byteSize: file.size,
          contentType: file.type,
        });
        accepted += 1;
        void runUpload(tempId, file);
      }
    },
    [addUpload, disabled, runUpload, setError, validateFile],
  );

  const onRetry = useCallback(
    (tempUploadId: string) => {
      const file = filesRef.current.get(tempUploadId);
      if (!file) {
        // Original File is gone (e.g. the page was reloaded). Clear the
        // row and ask the user to drop again. Vietnamese reason inline.
        setError(
          tempUploadId,
          'Không còn dữ liệu tệp gốc. Hãy kéo thả lại.',
        );
        return;
      }
      setStatus(tempUploadId, 'pending');
      setProgress(tempUploadId, 0);
      void runUpload(tempUploadId, file);
    },
    [runUpload, setError, setProgress, setStatus],
  );

  return (
    <section aria-labelledby="asset-uploader-heading" className="space-y-3">
      <h3
        id="asset-uploader-heading"
        className="text-sm font-semibold text-slate-900"
      >
        Tải asset lên
      </h3>
      <div
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
        onClick={() => {
          if (disabled) return;
          inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
          disabled
            ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
            : isDragOver
              ? 'border-slate-900 bg-slate-100 text-slate-900'
              : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'
        }`}
      >
        <p className="text-sm font-medium">
          Kéo thả tệp vào đây hoặc nhấn để chọn
        </p>
        <p className="mt-1 text-xs">
          Định dạng:{' '}
          {packKind === 'sticker'
            ? 'PNG / JPEG / WebP'
            : 'GIF'}
          . Kích thước tối đa {formatBytes(MAX_ASSET_BYTES)}.
        </p>
        <p className="mt-1 text-xs">
          Đã có {currentAssetCount} / {MAX_ASSETS_PER_PACK} asset
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => {
            handleFilesWithRetry(e.target.files);
            // Reset the input so the same file can be picked again later.
            e.target.value = '';
          }}
        />
      </div>

      {uploads.length > 0 ? (
        <ul
          aria-label="Hàng đợi tải lên"
          className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white"
        >
          {uploads.map((u) => (
            <li
              key={u.tempUploadId}
              className="flex items-center gap-3 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">
                  {u.fileName}
                </p>
                <p className="truncate text-xs text-slate-500">
                  {formatBytes(u.byteSize)}
                  {u.sha256 ? (
                    <>
                      {' '}
                      · sha256 <code className="font-mono">
                        {u.sha256.slice(0, 12)}…
                      </code>
                    </>
                  ) : null}
                </p>
                {u.status === 'uploading' ? (
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full bg-slate-900 transition-[width] duration-150"
                      style={{ width: `${Math.round(u.progress * 100)}%` }}
                    />
                  </div>
                ) : null}
                {u.status === 'error' && u.error ? (
                  <p className="mt-1 text-xs text-red-600" role="alert">
                    {u.error}
                  </p>
                ) : null}
              </div>
              <UploadStatusBadge status={u.status} progress={u.progress} />
              {u.status === 'error' ? (
                <button
                  type="button"
                  onClick={() => onRetry(u.tempUploadId)}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Thử lại
                </button>
              ) : null}
              {u.status === 'done' || u.status === 'error' ? (
                <button
                  type="button"
                  onClick={() => {
                    filesRef.current.delete(u.tempUploadId);
                    removeUpload(u.tempUploadId);
                  }}
                  aria-label={`Xoá ${u.fileName} khỏi hàng đợi`}
                  className="rounded-md p-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                >
                  ✕
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function UploadStatusBadge({
  status,
  progress,
}: {
  status: 'pending' | 'hashing' | 'uploading' | 'done' | 'error';
  progress: number;
}): JSX.Element {
  const map: Record<typeof status, { label: string; classes: string }> = {
    pending: {
      label: 'Đang chờ',
      classes: 'bg-slate-100 text-slate-700',
    },
    hashing: {
      label: 'Đang tính SHA-256',
      classes: 'bg-amber-50 text-amber-700',
    },
    uploading: {
      label: `Đang tải ${Math.round(progress * 100)}%`,
      classes: 'bg-blue-50 text-blue-700',
    },
    done: {
      label: 'Hoàn tất',
      classes: 'bg-emerald-50 text-emerald-700',
    },
    error: {
      label: 'Lỗi',
      classes: 'bg-red-50 text-red-700',
    },
  };
  const { label, classes } = map[status];
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
    >
      {label}
    </span>
  );
}
